"use client"

import React from 'react'
import { Moon, Sun } from "lucide-react"
import { useTheme } from "next-themes"
import { Button } from "../../components/ui/button"
import { useEffect, useState } from "react"
import { useTranslations } from 'next-intl'

export function ThemeToggle() {
  const t = useTranslations('a11y')
  // Use resolvedTheme so "system" is resolved to "light" or "dark" on the client
  const { resolvedTheme, setTheme } = useTheme()
  const [mounted, setMounted] = useState(false)
  // track whether the document currently has the 'dark' class; initialize false
  // initialize based on resolvedTheme (client) or document class when available
  const [docIsDark, setDocIsDark] = useState(() => {
    try {
      // prefer resolvedTheme if available (helps tests and system resolution)
      if (resolvedTheme) return resolvedTheme === 'dark'
      if (typeof document !== 'undefined') return document.documentElement.classList.contains('dark')
    } catch {
      /* ignore */
    }
    return false
  })

  useEffect(() => {
    // Delay setting mounted until the next tick so the component remains
    // renderable as 'null' during a synchronous server-like render in tests.
    const id = setTimeout(() => setMounted(true), 0)
    return () => clearTimeout(id)
  }, [])

  // After we've mounted on the client, read the real DOM and observe changes
  // (this useEffect is declared unconditionally to avoid changing Hooks order)
  useEffect(() => {
    if (!mounted) return
    if (typeof document === 'undefined') return

  // initialize: prefer resolvedTheme, fall back to document class
  setDocIsDark(resolvedTheme === 'dark' || document.documentElement.classList.contains('dark'))

    const obs = new MutationObserver(() => {
      // update based on document class and resolvedTheme
      setDocIsDark(resolvedTheme === 'dark' || document.documentElement.classList.contains('dark'))
    })
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
    return () => obs.disconnect()
  }, [mounted])

  // Don't render theme-dependent UI on the server
  if (!mounted) return null

  // The destination theme is what will be applied when the button is clicked
  const destinationTheme = docIsDark ? 'light' : 'dark'

  const handleToggle = () => setTheme(destinationTheme)
  const ariaLabel = destinationTheme === 'dark' ? t('switchToDark') : t('switchToLight')

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={handleToggle}
      className="focus-ring relative"
      data-testid="theme-toggle"
      title={ariaLabel}
      role="switch"
      aria-checked={docIsDark}
      aria-label={ariaLabel}
    >
      {/* Show the icon for the destination theme (the theme that will be applied on click) */}
      {destinationTheme === 'dark' ? (
        <Moon className="h-5 w-5" />
      ) : (
        <Sun className="h-5 w-5" />
      )}
      <span className="sr-only">{ariaLabel}</span>
    </Button>
  )
}